import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Row, Col, Table, Tag, Spin, Empty, Typography } from 'antd'
import '../../styles/aurora-page.css'
import {
  FileTextOutlined,
  ApiOutlined,
  DesktopOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import ReactECharts from 'echarts-for-react'
import * as dashboardApi from '@/api/dashboard'
import type { CaseTrendPoint, RecentReport } from '@/api/dashboard'
import type { DashboardStats } from '@/types'
import { useThemeStore, themes } from '@/stores/theme'
import { useProjectStore } from '@/stores/project'

const { Title } = Typography

/** 报告运行结果映射（1=成功/2=失败） */
const resultColor: Record<number, string> = {
  1: 'success',
  2: 'error',
}

const resultText: Record<number, string> = {
  1: '成功',
  2: '失败',
}

/** 任务类型映射（1=接口场景测试/2=UI测试） */
const taskTypeText: Record<number, { label: string; color: string }> = {
  1: { label: '接口场景', color: 'blue' },
  2: { label: 'UI 测试', color: 'purple' },
}

/** 用例类型图表配色（与报告类型标签色一致；功能用例跟随主题色） */
const chartColors = {
  scenario: '#3b82f6',
  ui: '#8B5CF6',
}

/** 统计卡片组件 */
function StatCard({
  icon,
  title,
  value,
  suffix,
  iconBg,
  iconColor,
  valueColor,
}: {
  icon: React.ReactNode
  title: string
  value: number | string
  suffix?: string
  iconBg: string
  iconColor: string
  valueColor?: string
}) {
  return (
    <div
      className="stat-card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <div className="stat-card-icon" style={{ background: iconBg, color: iconColor }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div className="stat-card-title">{title}</div>
        <div className="stat-card-value" style={{ color: valueColor }}>
          {value}
          {suffix && (
            <span style={{ fontSize: 16, fontWeight: 500, marginLeft: 4 }}>{suffix}</span>
          )}
        </div>
      </div>
    </div>
  )
}

/** 通过率圆环配置 */
function buildRingOption(rate: number, color: string, isDark: boolean) {
  const safe = Math.min(Math.max(rate || 0, 0), 100)
  return {
    series: [
      {
        type: 'pie',
        radius: ['65%', '85%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        silent: true,
        label: {
          show: true,
          position: 'center',
          formatter: `{a|${safe}%}\n{b|通过率}`,
          rich: {
            a: { fontSize: 26, fontWeight: 'bold', color },
            b: { fontSize: 12, color: isDark ? '#94a3b8' : '#7d8597', padding: [8, 0, 0, 0] },
          },
        },
        labelLine: { show: false },
        data: [
          { value: safe, itemStyle: { color } },
          { value: 100 - safe, itemStyle: { color: isDark ? '#334155' : '#f4f1ea' } },
        ],
      },
    ],
  }
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [recent, setRecent] = useState<RecentReport[]>([])
  const [trend, setTrend] = useState<CaseTrendPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [noProject, setNoProject] = useState(false)
  const currentProjectId = useProjectStore((s) => s.currentProject?.id)
  const fetchProjects = useProjectStore((s) => s.fetchProjects)
  const { isDark, current } = useThemeStore()
  /** 当前主题色板（跟随主题切换） */
  const themeColors = (themes.find((t) => t.key === current) || themes[0]).colors

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      try {
        let pid = currentProjectId
        if (!pid) {
          await fetchProjects()
          pid = useProjectStore.getState().currentProject?.id
        }
        if (cancelled) return
        setNoProject(!pid)
        if (!pid) return
        const [s, r, t] = await Promise.all([
          dashboardApi.getStats(pid),
          dashboardApi.getRecentReports(10, pid),
          dashboardApi.getCaseTrend(30, pid),
        ])
        if (cancelled) return
        setStats(s)
        setRecent(r as RecentReport[])
        setTrend(t as CaseTrendPoint[])
      } catch (e) {
        console.error('[Dashboard] 数据加载失败:', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [currentProjectId, fetchProjects])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (noProject) {
    return (
      <div className="aurora-page">
        <div className="page-header">
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
            工作台
          </Title>
        </div>
        <Empty description="暂无项目，请先创建项目" style={{ padding: 100 }} />
      </div>
    )
  }

  /** 折线 series 公共样式 */
  const lineSeries = (name: string, data: number[], color: string) => ({
    name,
    type: 'line',
    smooth: true,
    symbol: 'circle',
    symbolSize: 5,
    showSymbol: false,
    data,
    lineStyle: { color, width: 2 },
    itemStyle: { color },
    emphasis: { focus: 'series' },
  })

  /** 用例增长趋势图配置（三条线） */
  const trendOption = {
    tooltip: { trigger: 'axis' },
    legend: {
      bottom: 0,
      icon: 'roundRect',
      itemWidth: 14,
      itemHeight: 4,
      textStyle: { fontSize: 11, color: isDark ? '#94a3b8' : '#3d405b' },
    },
    grid: { left: 40, right: 20, top: 28, bottom: 40 },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: trend.map((t) => t.date),
      axisLabel: { fontSize: 11, color: isDark ? '#94a3b8' : '#3d405b' },
      axisLine: { lineStyle: { color: isDark ? '#334155' : '#e8e4dc' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: '新增用例数',
      minInterval: 1,
      nameTextStyle: { fontSize: 11, color: isDark ? '#94a3b8' : '#7d8597' },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: isDark ? 'rgba(51, 65, 85, 0.5)' : '#f4f1ea' } },
    },
    series: [
      lineSeries('功能用例', trend.map((t) => t.functional), themeColors.primary),
      lineSeries('接口场景用例', trend.map((t) => t.scenario), chartColors.scenario),
      lineSeries('UI 用例', trend.map((t) => t.ui), chartColors.ui),
    ],
  }

  const recentColumns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '报告名称',
      dataIndex: 'name',
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: '类型',
      dataIndex: 'task_type',
      width: 110,
      render: (v: number) => {
        const meta = taskTypeText[v]
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : '-'
      },
    },
    {
      title: '结果',
      dataIndex: 'run_result',
      width: 90,
      render: (v: number) => <Tag color={resultColor[v]}>{resultText[v] || v}</Tag>,
    },
    {
      title: '通过率',
      dataIndex: 'pass_rate',
      width: 100,
      render: (v: number) => `${v ?? 0}%`,
    },
    {
      title: '耗时',
      dataIndex: 'duration_ms',
      width: 100,
      render: (ms: number) => (ms ? `${ms} ms` : '-'),
    },
    {
      title: '运行时间',
      dataIndex: 'run_time',
      width: 180,
      render: (t: string | null) => (t ? new Date(t).toLocaleString('zh-CN') : '-'),
    },
  ]

  return (
    <div className="aurora-page">
      {/* 页面标题 */}
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          工作台
        </Title>
      </div>

      {/* 顶部统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <StatCard
            icon={<FileTextOutlined />}
            title="功能用例"
            value={stats?.functional_case_count || 0}
            iconBg="var(--au-primary-50)"
            iconColor="var(--au-primary)"
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <StatCard
            icon={<ApiOutlined />}
            title="接口场景用例"
            value={stats?.scenario_case_count || 0}
            iconBg="rgba(59, 130, 246, 0.1)"
            iconColor={chartColors.scenario}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <StatCard
            icon={<DesktopOutlined />}
            title="UI 用例"
            value={stats?.ui_case_count || 0}
            iconBg="rgba(139, 92, 246, 0.1)"
            iconColor={chartColors.ui}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <StatCard
            icon={<CheckCircleOutlined />}
            title="总通过率"
            value={stats?.pass_rate || 0}
            suffix="%"
            iconBg="rgba(16, 185, 129, 0.1)"
            iconColor="var(--success)"
            valueColor="var(--success)"
          />
        </Col>
      </Row>

      {/* 中部图表 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <div className="content-card">
            <div className="content-card-header">
              <div className="content-card-title">
                <RiseOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
                用例增长趋势（近 30 天）
              </div>
            </div>
            <div className="content-card-body">
              {trend.length > 0 ? (
                <ReactECharts option={trendOption} style={{ height: 280 }} />
              ) : (
                <Empty description="暂无数据" style={{ padding: 60 }} />
              )}
            </div>
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <div className="content-card">
            <div className="content-card-header">
              <div className="content-card-title">测试通过率</div>
            </div>
            <div className="content-card-body" style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 13,
                    color: isDark ? '#94a3b8' : '#7d8597',
                  }}
                >
                  接口场景报告
                </div>
                <ReactECharts
                  option={buildRingOption(stats?.scenario_pass_rate || 0, chartColors.scenario, isDark)}
                  style={{ height: 240 }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 13,
                    color: isDark ? '#94a3b8' : '#7d8597',
                  }}
                >
                  UI 报告
                </div>
                <ReactECharts
                  option={buildRingOption(stats?.ui_pass_rate || 0, chartColors.ui, isDark)}
                  style={{ height: 240 }}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* 底部最近报告 */}
      <div className="content-card">
        <div className="content-card-header">
          <div className="content-card-title">
            <ClockCircleOutlined style={{ marginRight: 8, color: 'var(--au-primary)' }} />
            最近测试报告
          </div>
        </div>
        <div className="content-card-body" style={{ padding: '0 0 20px' }}>
          <Table
            columns={recentColumns}
            dataSource={recent}
            rowKey="id"
            pagination={false}
            size="middle"
            locale={{ emptyText: <Empty description="暂无测试报告" /> }}
            style={{ marginTop: 0 }}
            onRow={(record) => ({
              onClick: () => navigate(`/reports?id=${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      </div>
    </div>
  )
}
